import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Alert,
  Button,
  Checkbox,
  InputText,
  Select,
  type SelectOption,
  SectionCard,
  PageWrapper,
} from "@/deps/ui-library";
import { api } from "@/api";
import {
  useAccounts,
  useApproveDebitNote,
  useCorrectableBills,
  useSaveDebitNote,
  useTaxCodes,
} from "@/api/queries";
import { DEBIT_NOTE_REASONS, type DebitNote, type NewDebitNote, type Party } from "@/api/types";
import { useCan } from "@/app/session";
import { LineEditor, type EditableLine } from "@/components/LineEditor/LineEditor";
import { PageHeader } from "@/components/PageHeader";
import { PartyCombobox } from "@/components/PartyCombobox";
import { SegmentControl } from "@/components/SegmentControl";
import { today } from "@/lib/dates";
import { calculateTotals } from "@/lib/invoiceTotals";
import { formatAmount } from "@/lib/money";
import { useTaxWords } from "@/app/session";
import { DateField } from "@/components/DateField";
import { documentLineAccounts } from "@/lib/accountPickers";

type Mode = "bill" | "free";

function chosen(details: { value: SelectOption | SelectOption[] | null }): string {
  const option = Array.isArray(details.value) ? details.value[0] : details.value;
  return option ? String(option.value) : "";
}

/**
 * A debit note reduces what you owe a supplier.
 *
 * It corrects a bill, or with no bill sits on the supplier's account. A bill is never edited once
 * approved, so this is how one is corrected: the note takes a number of its own and reverses the
 * expense and the tax on the lines it names.
 *
 * Given a draft and its supplier, it edits that draft.
 */
export function NewDebitNotePage({
  editing,
  billFromLink,
}: { editing?: { note: DebitNote; supplier: Party }; billFromLink?: string } = {}) {
  const say = useTaxWords();
  const navigate = useNavigate();
  const can = useCan();

  const { data: accounts } = useAccounts();
  const { data: taxCodes } = useTaxCodes();
  const save = useSaveDebitNote();
  const approve = useApproveDebitNote();

  const draft = editing?.note;
  const [savedId, setSavedId] = useState<string | undefined>(draft?.id);
  const [mode, setMode] = useState<Mode>(draft && !draft.againstBillId ? "free" : "bill");
  const [supplier, setSupplier] = useState<Party | null>(editing?.supplier ?? null);
  const [billId, setBillId] = useState(draft?.againstBillId ?? billFromLink ?? "");
  const [date, setDate] = useState(draft?.date ?? today());
  const [reason, setReason] = useState<string>(draft?.reason ?? DEBIT_NOTE_REASONS[0]);
  const [detail, setDetail] = useState(draft?.reasonDetail ?? "");
  const [recoverable, setRecoverable] = useState(
    draft ? Number(draft.nonRecoverableTax) === 0 : true,
  );
  const [lines, setLines] = useState<EditableLine[]>(
    draft
      ? draft.lines.map((line) => ({
          id: String(line.n),
          description: line.description,
          accountId: line.accountId,
          category: "S",
          rate: "13",
          grossPrice: line.unitPrice,
          quantity: line.quantity,
        }))
      : [
          {
            id: "1",
            description: "",
            accountId: "",
            category: "S",
            rate: "13",
            grossPrice: "",
            quantity: "1",
          },
        ],
  );

  const { data: allBills } = useCorrectableBills(undefined, mode === "bill");
  const { data: supplierBills } = useCorrectableBills(
    supplier?.id,
    mode === "bill" && supplier !== null,
  );
  const bills = supplier === null ? (allBills ?? []) : (supplierBills ?? []);
  const bill = allBills?.find((b) => b.id === billId);

  // Choosing a bill names its supplier, so the two cannot disagree.
  const billSupplierId = bill?.supplierId;
  const billSupplierName = bill?.supplierName;
  useEffect(() => {
    if (billSupplierId === undefined) return;
    void api.parties.search("supplier", billSupplierName ?? "").then((found) => {
      setSupplier((current) => current ?? found.find((p) => p.id === billSupplierId) ?? null);
    });
  }, [billSupplierId, billSupplierName]);

  // Active ones, never a required account: control, tax and the rest Rigo posts itself (ADR-0066).
  const offered = documentLineAccounts(accounts);
  const expenseAccounts = [
    ...offered.filter((a) => a.group === "Expenses"),
    ...offered.filter((a) => a.group === "Assets"),
  ];
  const withAccounts = lines.map((line) =>
    line.accountId === "" ? { ...line, accountId: expenseAccounts[0]?.id ?? "" } : line,
  );

  const totals = calculateTotals(withAccounts);
  const vat = taxCodes?.find((code) => code.recoverable && code.ratePct === "13");
  const blocked = taxCodes?.find((code) => !code.recoverable && code.ratePct === "13");
  const taxCodeId = (recoverable ? vat?.id : blocked?.id) ?? "";

  // Against a bill it is limited to what the bill was for. With no bill, nothing limits it (ADR-0042).
  const overTotal = bill !== undefined && Number(totals.totalWithVat) > Number(bill.total);
  const overBill =
    bill !== undefined && !overTotal && Number(totals.totalWithVat) > Number(bill.balance);
  const ready =
    supplier !== null &&
    (mode === "free" || bill !== undefined) &&
    !overTotal &&
    taxCodeId !== "" &&
    withAccounts.every((l) => l.accountId !== "") &&
    Number(totals.totalWithVat) > 0;
  const pending = save.isPending || approve.isPending;
  const refusal = save.error ?? approve.error;

  const dto = (): NewDebitNote => ({
    id: savedId,
    supplierId: supplier!.id,
    date,
    againstBillId: mode === "bill" ? billId : null,
    reason,
    reasonDetail: detail.trim(),
    lines: withAccounts.map((line) => ({
      description: line.description,
      accountId: line.accountId,
      quantity: line.quantity,
      unitPrice: line.grossPrice,
      taxCodeId,
      capital: accounts?.find((account) => account.id === line.accountId)?.group === "Assets",
    })),
  });

  return (
    <PageWrapper spacing="md" className="p-0">
      <PageHeader
        title={draft ? "Edit debit note" : "New debit note"}
        actions={
          <span className="flex gap-2">
            <Button
              colorScheme="default"
              variant="ghost"
              size="sm"
              onClick={() =>
                void navigate(
                  draft
                    ? { to: "/purchases/debit-notes/$id", params: { id: draft.id } }
                    : { to: "/purchases/debit-notes" },
                )
              }
            >
              Cancel
            </Button>
            <Button
              colorScheme="default"
              variant="outline"
              size="sm"
              disabled={!ready || pending}
              onClick={() =>
                save.mutate(dto(), {
                  onSuccess: (note) =>
                    void navigate({ to: "/purchases/debit-notes/$id", params: { id: note.id } }),
                })
              }
            >
              {save.isPending ? "Saving" : "Save draft"}
            </Button>
            {can("purchases.debitnotes.approve") ? (
              <Button
                colorScheme="primary"
                variant="solid"
                size="sm"
                disabled={!ready || pending}
                onClick={() =>
                  // Saved first, so a refusal keeps a draft. It stays here with the reason on screen,
                  // and the next press revises that draft rather than adding a copy.
                  save.mutate(dto(), {
                    onSuccess: (note) => {
                      setSavedId(note.id);
                      approve.mutate(note.id, {
                        onSuccess: () =>
                          void navigate({
                            to: "/purchases/debit-notes/$id",
                            params: { id: note.id },
                          }),
                      });
                    },
                  })
                }
              >
                {approve.isPending ? "Issuing" : "Issue debit note"}
              </Button>
            ) : null}
          </span>
        }
      />

      {refusal ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={
            refusal instanceof Error ? refusal.message : "That debit note could not be saved."
          }
        />
      ) : null}

      <SegmentControl<Mode>
        label="Debit note against"
        segments={[
          { value: "bill", label: "Against a bill" },
          { value: "free", label: "Supplier's account" },
        ]}
        value={mode}
        onChange={(next) => {
          setMode(next);
          setBillId("");
        }}
      />

      <SectionCard title="Debit note">
        <div className="grid gap-3 md:grid-cols-2">
          <PartyCombobox
            label="Supplier"
            required
            value={supplier}
            onChange={(party) => {
              setSupplier(party);
              setBillId("");
            }}
            search={(q) => api.parties.search("supplier", q)}
          />

          {mode === "bill" ? (
            <Select
              label="Against bill"
              required
              options={bills.map((option) => ({
                value: option.id,
                label: `${option.number} · ${option.supplierName} · ${formatAmount(option.balance)}`,
              }))}
              value={billId}
              onChange={(details) => setBillId(chosen(details))}
              showClearButton={false}
            />
          ) : (
            <p className="text-fg-muted self-end text-xs">
              No preceding bill. The debit sits on the supplier's account, and can be used on a
              later bill.
            </p>
          )}

          <DateField label="Date" required value={date} onChange={setDate} />

          <Select
            label="Reason"
            required
            options={DEBIT_NOTE_REASONS.map((option) => ({ value: option, label: option }))}
            value={reason}
            onChange={(details) => {
              const picked = chosen(details);
              if (picked) setReason(picked);
            }}
            showClearButton={false}
          />

          <div className="md:col-span-2">
            <InputText
              label="Details · shown on the document"
              value={detail}
              maxLength={500}
              onChange={(event) => setDetail(event.target.value)}
            />
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Lines">
        <LineEditor lines={withAccounts} onChange={setLines} accounts={expenseAccounts} />
      </SectionCard>

      <SectionCard title="Tax">
        <Checkbox
          label="Input tax on this debit note is recoverable"
          checked={recoverable}
          onCheckedChange={(details) => setRecoverable(details.checked === true)}
        />
        <dl className="mt-3 flex flex-col gap-2 text-sm">
          <div className="flex items-baseline justify-between">
            <dt className="text-fg-muted">Subtotal</dt>
            <dd className="num text-fg">{formatAmount(totals.totalWithoutVat)}</dd>
          </div>
          <div className="flex items-baseline justify-between">
            <dt className="text-fg-muted">
              {recoverable ? say("Recoverable VAT reversed") : "Non-recoverable tax reversed"}
            </dt>
            <dd className="num text-fg">{formatAmount(totals.totalVatAmount)}</dd>
          </div>
          <div className="border-line-soft flex items-baseline justify-between border-t pt-2 font-semibold">
            <dt className="text-fg">Total debit</dt>
            <dd className="num text-fg">{formatAmount(totals.totalWithVat)}</dd>
          </div>
        </dl>
        {!recoverable ? (
          <p className="text-fg-muted mt-3 text-xs">
            {say(
              "Non-recoverable tax reverses out of the expense account on its line, not the input-VAT account. The supplier is owed less by the same total either way.",
            )}
          </p>
        ) : null}
        {overTotal ? (
          <p role="alert" className="text-bad-text mt-3 text-xs">
            A debit note can't be more than its bill. {bill.number} was for{" "}
            {formatAmount(bill.total)}.
          </p>
        ) : null}
        {overBill ? (
          <p className="text-warn-text mt-3 text-xs">
            {bill.number} has {formatAmount(bill.balance)} outstanding. The rest, up to{" "}
            {formatAmount(Number(totals.totalWithVat) - Math.max(Number(bill.balance), 0))}, is held
            in credit from the supplier and can be used on a later bill.
          </p>
        ) : null}
      </SectionCard>
    </PageWrapper>
  );
}
